import { ESC_NAME } from '@anm/constants/keyCodes';
import useSwitchState from '@anm/hooks/useSwitchState';
import { useEffect, useRef, useState, ChangeEvent, FormEvent, FC, KeyboardEvent } from 'react';

import Wrapper, { ClearIcon, Input, SearchIcon } from './Wrapper';

export * from './Wrapper';

export type SearchProps = {
  text?: string;
  placeholder?: string;
  maxLength?: number;
  isAlwaysOpen?: boolean;
  onSearch: (searchText: string) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  onOpen?: () => void;
  onClose?: () => void;
};

const Search: FC<SearchProps> = ({
  text,
  maxLength,
  onBlur,
  onFocus,
  onOpen,
  onClose,
  onSearch,
  isAlwaysOpen,
  placeholder = 'Search'
}) => {
  const [value, setValue] = useState(text);
  const [isOpen, open, close] = useSwitchState(isAlwaysOpen);
  const prevValueRef = useRef<string>();

  const handleChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
    setValue(target.value);
  };

  const handleSearch = () => {
    if (!isOpen) {
      handleOpen();
      return;
    }

    if (!value && isOpen) {
      close();
      onClose?.();
      return;
    }

    value && prevValueRef.current !== value && onSearch(value);
    prevValueRef.current = value;
  };

  const handleOpen = () => {
    open();
    onOpen?.();
  };

  const handleClose = () => {
    setValue('');
    close();
    prevValueRef.current = '';
    onClose?.();
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    handleSearch();
  };

  const handleKeyUp = (event: KeyboardEvent) => {
    if (event.code === ESC_NAME) !!value ? setValue('') : handleClose();
  };

  useEffect(() => {
    if (isAlwaysOpen) return;

    text ? handleOpen() : handleClose();
    setValue(text);
  }, [text]);

  return (
    <Wrapper>
      <SearchIcon src="images/icons/search_filter_v1.svg" onClick={handleSearch} alt="search Icon" />
      {isOpen && (
        <>
          <form onSubmit={handleSubmit}>
            <Input
              autoFocus
              type="text"
              value={value}
              maxLength={maxLength || 60}
              onKeyUp={handleKeyUp}
              onChange={handleChange}
              {...{ onFocus, onBlur, placeholder }}
            />
          </form>
          {!isAlwaysOpen && <ClearIcon src="images/icons/cross_clear_ic.svg" onClick={handleClose} alt="clear icon" />}{' '}
        </>
      )}
    </Wrapper>
  );
};

export default Search;
